import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';

export type SupportedLanguage = 'de' | 'en' | 'ar' | 'es' | 'fr' | 'it' | 'pl' | 'ro' | 'ru' | 'tr' | 'uk';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'tremaze-language';
  private readonly supportedLanguages: SupportedLanguage[] = ['de', 'en', 'ar', 'es', 'fr', 'it', 'pl', 'ro', 'ru', 'tr', 'uk'];
  private currentLanguageSubject: BehaviorSubject<SupportedLanguage>;

  constructor(
    private translateService: TranslateService,
    private cookieService: CookieService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(REQUEST) private request?: any
  ) {
    // Initialisiere die Sprache aus dem Cookie oder verwende Default
    const savedLanguage = this.getSavedLanguage();
    this.currentLanguageSubject = new BehaviorSubject<SupportedLanguage>(
      savedLanguage
    );

    // Setze verfügbare Sprachen
    this.translateService.addLangs(this.supportedLanguages);

    // Setze die initiale Sprache
    this.setLanguage(savedLanguage, false);
  }

  /**
   * Gibt die aktuell aktive Sprache als Observable zurück
   */
  get currentLanguage$(): Observable<SupportedLanguage> {
    return this.currentLanguageSubject.asObservable();
  }

  /**
   * Gibt die aktuell aktive Sprache zurück
   */
  get currentLanguage(): SupportedLanguage {
    return this.currentLanguageSubject.value;
  }

  /**
   * Gibt alle verfügbaren Sprachen zurück
   */
  get availableLanguages(): SupportedLanguage[] {
    return this.supportedLanguages;
  }

  /**
   * Setzt die aktive Sprache
   * @param language Die zu setzende Sprache
   * @param persist Ob die Sprache im Cookie gespeichert werden soll (default: true)
   */
  setLanguage(language: SupportedLanguage, persist = true): void {
    if (!this.supportedLanguages.includes(language)) {
      console.warn(
        `Language "${language}" is not supported. Falling back to "de".`
      );
      language = 'de';
    }

    this.translateService.use(language);
    this.currentLanguageSubject.next(language);

    if (persist) {
      this.saveLanguage(language);
    }
  }

  /**
   * Wechselt zur nächsten verfügbaren Sprache
   */
  toggleLanguage(): void {
    const currentIndex = this.supportedLanguages.indexOf(this.currentLanguage);
    const nextIndex = (currentIndex + 1) % this.supportedLanguages.length;
    this.setLanguage(this.supportedLanguages[nextIndex]);
  }

  /**
   * Versucht die Browser-Sprache zu setzen, falls unterstützt
   */
  useBrowserLanguage(): void {
    const browserLang = this.translateService.getBrowserLang();
    const supportedLang = this.supportedLanguages.find(
      (lang) => lang === browserLang
    );

    if (supportedLang) {
      this.setLanguage(supportedLang);
    } else {
      console.info(
        `Browser language "${browserLang}" not supported. Using default "de".`
      );
      this.setLanguage('de');
    }
  }

  /**
   * Speichert die Sprache im Cookie (funktioniert auf Client und Server)
   */
  private saveLanguage(language: SupportedLanguage): void {
    try {
      this.cookieService.put(this.STORAGE_KEY, language);
    } catch (error) {
      console.warn('Failed to save language to cookie:', error);
    }
  }

  /**
   * Lädt die gespeicherte Sprache aus dem Cookie (funktioniert auf Client und Server)
   */
  private getSavedLanguage(): SupportedLanguage {
    // 1. Versuche Cookie zu lesen (funktioniert auf Client und Server)
    try {
      const cookieLang = this.cookieService.get(this.STORAGE_KEY) as SupportedLanguage;
      if (cookieLang && this.supportedLanguages.includes(cookieLang)) {
        return cookieLang;
      }
    } catch (error) {
      console.warn('Failed to load language from cookie:', error);
    }

    // 2. SSR: Versuche Accept-Language Header zu parsen
    if (isPlatformServer(this.platformId) && this.request) {
      const acceptLanguage = this.request.headers['accept-language'];
      if (acceptLanguage) {
        const preferredLang = this.parseAcceptLanguage(acceptLanguage);
        if (preferredLang) {
          return preferredLang;
        }
      }
    }

    // 3. Client: Versuche Browser-Sprache
    if (isPlatformBrowser(this.platformId)) {
      const browserLang = this.translateService.getBrowserLang();
      if (this.supportedLanguages.includes(browserLang as SupportedLanguage)) {
        return browserLang as SupportedLanguage;
      }
    }

    // 4. Fallback auf Deutsch
    return 'de';
  }

  /**
   * Parst den Accept-Language Header und gibt die erste unterstützte Sprache zurück
   * Format: "en-US,en;q=0.9,de;q=0.8"
   */
  private parseAcceptLanguage(header: string): SupportedLanguage | null {
    try {
      // Parse und sortiere nach Quality-Wert (q)
      const languages = header
        .split(',')
        .map(lang => {
          const parts = lang.split(';');
          const code = parts[0].trim().split('-')[0].toLowerCase();
          const quality = parts[1] ? parseFloat(parts[1].split('=')[1]) : 1.0;
          return { code, quality };
        })
        .sort((a, b) => b.quality - a.quality);

      // Finde erste unterstützte Sprache
      for (const lang of languages) {
        if (this.supportedLanguages.includes(lang.code as SupportedLanguage)) {
          return lang.code as SupportedLanguage;
        }
      }
    } catch (error) {
      console.warn('Failed to parse Accept-Language header:', error);
    }
    return null;
  }

  /**
   * Übersetzt einen Key sofort (nicht als Observable)
   * @param key Der Übersetzungs-Key
   * @param params Optionale Parameter für die Interpolation
   */
  instant(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  /**
   * Übersetzt einen Key als Observable
   * @param key Der Übersetzungs-Key
   * @param params Optionale Parameter für die Interpolation
   */
  get(key: string, params?: any): Observable<string> {
    return this.translateService.get(key, params);
  }
}
